import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Pencil, Search, Layers, Boxes, Trash2, ChevronDown, ChevronRight, ChevronLeft, Upload, X, History, Eye, Scissors, Link2 } from "lucide-react";
import { AdjustmentHistoryDialog } from "./AdjustmentHistory";
import { FabricCuttingReferencesDialog } from "../../components/production/FabricCuttingReferencesDialog";
import { toast } from "sonner";
import { api, formatApiError, fmtIDR } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { PageHeader, Panel } from "../../lib/ui";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";

const num = (n) => (Number(n) || 0).toLocaleString("id-ID", { maximumFractionDigits: 2 });

export default function Fabrics() {
  const { can, user } = useAuth();
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [active, setActive] = useState("all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [stockFilter, setStockFilter] = useState("all");
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState({});
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [stockFor, setStockFor] = useState(null);
  const [histFor, setHistFor] = useState(null);
  const [detailFor, setDetailFor] = useState(null);
  const [cutFor, setCutFor] = useState(null);
  const [cuttingReferencesFor, setCuttingReferencesFor] = useState(null);
  const [warehouses, setWarehouses] = useState([]);
  const write = can("production_admin", "warehouse_admin", "rnd");

  const load = () => api.get("/production/fabrics", { params: { q: q || undefined, active: active === "all" ? undefined : active } }).then((r) => setList(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [active]);
  useEffect(() => { api.get("/warehouses", { params: { usage: "material" } }).then((r) => setWarehouses(r.data)); const t = setTimeout(load, 350); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);
  useEffect(() => { setPage(1); }, [q, active, pageSize]);

  useEffect(() => { setPage(1); }, [q, active, pageSize, stockFilter]);

  const LOW_YARD = 10;
  const filtered = list.filter((f) => {
    if (stockFilter === "all") return true;
    const t = Number(f.available_stock) || 0;
    const cols = f.colors || [];
    if (stockFilter === "habis") return t <= 0;
    if (stockFilter === "low") return cols.some((c) => { const a = Number(c.available_stock) || 0; return a > 0 && a <= LOW_YARD; });
    if (stockFilter === "available") return t > LOW_YARD;
    return true;
  });
  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const curPage = Math.min(page, totalPages);
  const paged = filtered.slice((curPage - 1) * pageSize, curPage * pageSize);

  const fixMigrated = async () => {
    if (!window.confirm("Perbaiki stok kain hasil migrasi? Sistem akan mengalikan jumlah roll × yard per roll (mis. 1 roll × 100 yd = 100 yd). Aman diulang.")) return;
    try {
      const r = await api.post("/production/fabrics/fix-migrated-roll-yards");
      toast.success(r.data.changed ? `Selesai: ${r.data.changed} baris roll diperbaiki (total ${num(r.data.after)} yd).` : "Tidak ada data migrasi yang perlu diperbaiki (sudah benar).");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="fabrics-page">
      <PageHeader title="Stok Kain" subtitle="Master kain berjenjang: Kain → Warna → Roll (satuan yard)">
        {write && <Button variant="outline" onClick={fixMigrated} className="border-taupe text-cocoa mr-2" data-testid="fix-migrated-btn"><History className="w-4 h-4 mr-1.5" /> Perbaiki Stok Migrasi</Button>}
        {write && <Button onClick={() => { setEdit(null); setOpen(true); }} data-testid="add-fabric-btn" className="bg-cocoa hover:bg-cocoa-dark text-ivory"><Plus className="w-4 h-4 mr-1.5" /> Kain Baru</Button>}
      </PageHeader>

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-wmuted" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari nama / kode / warna / produk..." className="pl-9 bg-white" data-testid="fabric-search" />
        </div>
        <Select value={active} onValueChange={setActive}>
          <SelectTrigger className="w-40 bg-white" data-testid="fabric-active-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Semua</SelectItem><SelectItem value="active">Aktif</SelectItem><SelectItem value="inactive">Nonaktif</SelectItem></SelectContent>
        </Select>
        <Select value={stockFilter} onValueChange={setStockFilter}>
          <SelectTrigger className="w-52 bg-white" data-testid="fabric-stock-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Sisa Stok</SelectItem>
            <SelectItem value="available">Tersedia (&gt; 10 yd)</SelectItem>
            <SelectItem value="low">Menipis (≤ 10 yd / warna)</SelectItem>
            <SelectItem value="habis">Habis (0 yd)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Panel>
        <div className="hidden 2xl:block">
          <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="w-8"></TableHead><TableHead>Kode</TableHead><TableHead>Nama Kain</TableHead><TableHead>Produk</TableHead>
              <TableHead className="text-center">Warna</TableHead><TableHead className="text-right">Available (Yard)</TableHead>
              <TableHead className="text-right">Cutting (Yard)</TableHead><TableHead className="text-right">Remaining (Yard)</TableHead>
              <TableHead>Status</TableHead><TableHead className="text-right">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paged.map((f) => (
              <React.Fragment key={f.id}>
                <TableRow data-testid={`fabric-row-${f.id}`} className="cursor-pointer" onClick={() => setExpanded((e) => ({ ...e, [f.id]: !e[f.id] }))}>
                  <TableCell>{expanded[f.id] ? <ChevronDown className="w-4 h-4 text-cocoa" /> : <ChevronRight className="w-4 h-4 text-wmuted" />}</TableCell>
                  <TableCell className="font-mono text-xs">{f.code}</TableCell>
                  <TableCell className="font-semibold text-cocoa">{f.name}{f.product_name && <span className="block text-[11px] font-normal text-wmuted">↳ {f.product_name}</span>}</TableCell>
                  <TableCell className="max-w-[220px]" onClick={(e) => e.stopPropagation()}>
                    {(f.used_by_products || []).length ? (
                      <div className="flex flex-wrap gap-1" title={f.used_by_products.map((p) => p.name).join(", ")}>
                        {f.used_by_products.slice(0, 3).map((p) => (
                          <button key={p.id || p.name} type="button" onClick={() => p.id && navigate(`/production/bom?product=${p.id}`)} className="inline-flex items-center rounded-full bg-cocoa/10 hover:bg-cocoa/20 text-cocoa px-2 py-0.5 text-[11px] transition-colors" title={`Buka BOM: ${p.name}`} data-testid={`fabric-product-col-${f.id}`}>{p.name}</button>
                        ))}
                        {f.used_by_products.length > 3 && <span className="text-[11px] text-wmuted self-center">+{f.used_by_products.length - 3}</span>}
                      </div>
                    ) : <span className="text-xs text-wmuted">—</span>}
                  </TableCell>
                  <TableCell className="text-center">{f.color_count}</TableCell>
                  <TableCell className={`text-right font-semibold ${(Number(f.available_stock) || 0) <= 0 ? "text-status-rejected" : (Number(f.available_stock) || 0) <= 10 ? "text-amber-600" : "text-cocoa"}`}>{num(f.available_stock)} yd</TableCell>
                  <TableCell className="text-right">{num(f.cutting_stock)}</TableCell>
                  <TableCell className="text-right">{num(f.remaining_stock)}</TableCell>
                  <TableCell><span className={f.active ? "text-status-passed" : "text-wmuted"}>{f.active ? "Aktif" : "Nonaktif"}</span></TableCell>
                  <TableCell className="text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    <Button size="icon" variant="ghost" title="Detail Stok" onClick={() => setDetailFor(f.id)} data-testid={`fabric-detail-${f.id}`}><Eye className="w-4 h-4 text-cocoa" /></Button>
                    {write && <>
                      <Button size="icon" variant="ghost" title="Proses Cutting (BOM + Cutting cepat)" onClick={() => setCutFor({ fabricId: f.id })} data-testid={`fabric-cut-${f.id}`}><Scissors className="w-4 h-4 text-cocoa" /></Button>
                      <Button size="icon" variant="ghost" title="Edit" onClick={() => { setEdit(f); setOpen(true); }} data-testid={`fabric-edit-${f.id}`}><Pencil className="w-4 h-4 text-cocoa" /></Button>
                      <Button size="icon" variant="ghost" title="Hapus" onClick={() => delFabric(f, load)} data-testid={`fabric-del-${f.id}`}><Trash2 className="w-4 h-4 text-status-rejected" /></Button>
                    </>}
                  </TableCell>
                </TableRow>
                {expanded[f.id] && (f.colors || []).map((c) => (
                  <TableRow key={c.id} className="bg-ivory/40" data-testid={`fabric-color-${c.id}`}>
                    <TableCell></TableCell>
                    <TableCell colSpan={3}>
                      <div className="flex items-center gap-2">
                        {c.image ? <img src={c.image} alt="" className="w-8 h-8 rounded object-cover border border-taupe/30" /> : <div className="w-8 h-8 rounded bg-taupe/20" />}
                        <span className="font-medium text-cocoa">{c.color_name}</span>
                        <span className="text-xs text-wmuted">{fmtIDR(c.price_per_yard)}/yd</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-center text-xs text-wmuted">{c.roll_count != null ? c.roll_count : (c.rolls || []).length} gulungan{c.length_kind_count > 1 ? ` · ${c.length_kind_count} panjang` : ""}</TableCell>
                    <TableCell className={`text-right ${(Number(c.available_stock) || 0) <= 0 ? "text-status-rejected font-semibold" : (Number(c.available_stock) || 0) <= 10 ? "text-amber-600 font-semibold" : ""}`}>{num(c.available_stock)} yd</TableCell>
                    <TableCell className="text-right">{num(c.cutting_stock)} yd</TableCell>
                    <TableCell className="text-right">{num(c.remaining_stock)} yd</TableCell>
                    <TableCell></TableCell>
                    <TableCell className="text-right">
                      {Number(c.cutting_stock) > 0 && (
                        <Button
                          data-testid={`fabric-cutting-references-${c.id}`}
                          onClick={() => setCuttingReferencesFor({
                            id: c.id,
                            fabricName: f.name,
                            colorName: c.color_name,
                          })}
                          size="icon"
                          title="Lihat referensi Cutting aktif"
                          variant="ghost"
                        >
                          <Link2 className="w-4 h-4 text-cocoa" />
                        </Button>
                      )}
                      {write && <>
                        <Button size="icon" variant="ghost" title="Proses Cutting" onClick={() => setCutFor({ fabricId: f.id, colorId: c.id })} data-testid={`fabric-color-cut-${c.id}`}><Scissors className="w-4 h-4 text-cocoa" /></Button>
                        <Button size="icon" variant="ghost" title="Set Stok" onClick={() => setStockFor({ id: c.id, name: `${f.name} - ${c.color_name}`, rolls: c.rolls })} data-testid={`fabric-setstock-${c.id}`}><Boxes className="w-4 h-4 text-cocoa" /></Button>
                        <Button size="icon" variant="ghost" title="Riwayat Penyesuaian" onClick={() => setHistFor({ material_id: c.id, name: `${f.name} - ${c.color_name}`, kind: "fabric" })} data-testid={`fabric-history-${c.id}`}><History className="w-4 h-4 text-cocoa" /></Button>
                      </>}
                    </TableCell>
                  </TableRow>
                ))}
              </React.Fragment>
            ))}
            {!total && <TableRow><TableCell colSpan={10} className="text-center text-wmuted py-10"><Layers className="w-6 h-6 mx-auto mb-2 opacity-50" />{list.length ? "Tidak ada kain sesuai filter." : "Belum ada data kain."}</TableCell></TableRow>}
          </TableBody>
          </Table>
        </div>
        <div className="divide-y divide-taupe/20 2xl:hidden" data-testid="fabric-mobile-list">
          {paged.map((fabric) => (
            <div className="space-y-3 p-4" data-testid={`fabric-mobile-card-${fabric.id}`} key={fabric.id}>
              <div className="flex items-start justify-between gap-3">
                <button
                  className="min-w-0 text-left"
                  data-testid={`fabric-mobile-toggle-${fabric.id}`}
                  onClick={() => setExpanded((state) => ({
                    ...state,
                    [fabric.id]: !state[fabric.id],
                  }))}
                  type="button"
                >
                  <div className="flex items-center gap-1 font-semibold text-cocoa">
                    {expanded[fabric.id] ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                    <span>{fabric.name}</span>
                  </div>
                  <div className="mt-1 font-mono text-xs text-wmuted">{fabric.code}</div>
                </button>
                <span className={fabric.active ? "text-sm text-status-passed" : "text-sm text-wmuted"}>
                  {fabric.active ? "Aktif" : "Nonaktif"}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-2 text-sm">
                <div>
                  <div className="text-xs text-wmuted">Available</div>
                  <div className="font-semibold text-cocoa">{num(fabric.available_stock)} yd</div>
                </div>
                <div>
                  <div className="text-xs text-wmuted">Cutting</div>
                  <div className="font-semibold text-cocoa">{num(fabric.cutting_stock)} yd</div>
                </div>
                <div>
                  <div className="text-xs text-wmuted">Remaining</div>
                  <div className="font-semibold text-cocoa">{num(fabric.remaining_stock)} yd</div>
                </div>
              </div>
              <div className="flex flex-wrap gap-1">
                <Button
                  data-testid={`fabric-mobile-detail-${fabric.id}`}
                  onClick={() => setDetailFor(fabric.id)}
                  size="icon"
                  title="Detail stok"
                  variant="ghost"
                >
                  <Eye className="h-4 w-4 text-cocoa" />
                </Button>
                {write && <>
                  <Button
                    data-testid={`fabric-mobile-cut-${fabric.id}`}
                    onClick={() => setCutFor({ fabricId: fabric.id })}
                    size="icon"
                    title="Proses Cutting"
                    variant="ghost"
                  >
                    <Scissors className="h-4 w-4 text-cocoa" />
                  </Button>
                  <Button
                    data-testid={`fabric-mobile-edit-${fabric.id}`}
                    onClick={() => {
                      setEdit(fabric);
                      setOpen(true);
                    }}
                    size="icon"
                    title="Edit kain"
                    variant="ghost"
                  >
                    <Pencil className="h-4 w-4 text-cocoa" />
                  </Button>
                  <Button
                    data-testid={`fabric-mobile-delete-${fabric.id}`}
                    onClick={() => delFabric(fabric, load)}
                    size="icon"
                    title="Hapus kain"
                    variant="ghost"
                  >
                    <Trash2 className="h-4 w-4 text-status-rejected" />
                  </Button>
                </>}
              </div>
              <div className="border-t border-taupe/20 pt-3" data-testid={`fabric-products-m-${fabric.id}`}>
                <div className="text-xs text-wmuted font-medium mb-1">Dipakai produk:</div>
                <div className="flex flex-wrap gap-1.5">
                  {(fabric.used_by_products || []).length
                    ? fabric.used_by_products.map((p) => (
                        <button key={p.id || p.name} type="button" onClick={() => p.id && navigate(`/production/bom?product=${p.id}`)} className="text-xs rounded-full bg-cocoa/10 hover:bg-cocoa/20 text-cocoa px-2 py-0.5 transition-colors">{p.name}</button>
                      ))
                    : <span className="text-xs text-wmuted italic">Belum ada produk (BOM aktif)</span>}
                </div>
              </div>
              {expanded[fabric.id] && (fabric.colors || []).map((color) => (
                <div
                  className="space-y-2 border-t border-taupe/20 pt-3"
                  data-testid={`fabric-mobile-color-${color.id}`}
                  key={color.id}
                >
                  <div className="flex items-center gap-2">
                    {color.image ? (
                      <img alt="" className="h-8 w-8 rounded object-cover" src={color.image} />
                    ) : (
                      <div className="h-8 w-8 rounded bg-taupe/20" />
                    )}
                    <div>
                      <div className="font-medium text-cocoa">{color.color_name}</div>
                      <div className="text-xs text-wmuted">{color.roll_count || 0} gulungan</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <span>Available: {num(color.available_stock)} yd</span>
                    <span>Cutting: {num(color.cutting_stock)} yd</span>
                    <span>Sisa: {num(color.remaining_stock)} yd</span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {Number(color.cutting_stock) > 0 && (
                      <Button
                        data-testid={`fabric-mobile-cutting-references-${color.id}`}
                        onClick={() => setCuttingReferencesFor({
                          id: color.id,
                          fabricName: fabric.name,
                          colorName: color.color_name,
                        })}
                        size="icon"
                        title="Lihat referensi Cutting aktif"
                        variant="ghost"
                      >
                        <Link2 className="h-4 w-4 text-cocoa" />
                      </Button>
                    )}
                    {write && <>
                      <Button
                        data-testid={`fabric-mobile-color-cut-${color.id}`}
                        onClick={() => setCutFor({ fabricId: fabric.id, colorId: color.id })}
                        size="icon"
                        title="Proses Cutting"
                        variant="ghost"
                      >
                        <Scissors className="h-4 w-4 text-cocoa" />
                      </Button>
                      <Button
                        data-testid={`fabric-mobile-setstock-${color.id}`}
                        onClick={() => setStockFor({
                          id: color.id,
                          name: `${fabric.name} - ${color.color_name}`,
                          rolls: color.rolls,
                        })}
                        size="icon"
                        title="Set stok"
                        variant="ghost"
                      >
                        <Boxes className="h-4 w-4 text-cocoa" />
                      </Button>
                      <Button
                        data-testid={`fabric-mobile-history-${color.id}`}
                        onClick={() => setHistFor({
                          material_id: color.id,
                          name: `${fabric.name} - ${color.color_name}`,
                          kind: "fabric",
                        })}
                        size="icon"
                        title="Riwayat penyesuaian"
                        variant="ghost"
                      >
                        <History className="h-4 w-4 text-cocoa" />
                      </Button>
                    </>}
                  </div>
                </div>
              ))}
            </div>
          ))}
          {!total && (
            <div className="py-10 text-center text-wmuted" data-testid="fabric-mobile-empty">
              {list.length ? "Tidak ada kain sesuai filter." : "Belum ada data kain."}
            </div>
          )}
        </div>
        {total > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-3 px-3 py-3 border-t border-taupe/15" data-testid="fabric-pagination">
            <div className="flex items-center gap-2 text-xs text-wmuted">
              <span>Baris per halaman</span>
              <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
                <SelectTrigger className="w-20 h-8 bg-white" data-testid="fabric-pagesize"><SelectValue /></SelectTrigger>
                <SelectContent>{[10, 20, 50, 100].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
              </Select>
              <span>Menampilkan {(curPage - 1) * pageSize + 1}–{Math.min(curPage * pageSize, total)} dari {total} kain</span>
            </div>
            <div className="flex items-center gap-1">
              <Button size="icon" variant="outline" className="h-8 w-8 border-taupe" disabled={curPage <= 1} onClick={() => setPage(curPage - 1)} data-testid="fabric-prev"><ChevronLeft className="w-4 h-4" /></Button>
              <span className="text-xs text-cocoa px-2" data-testid="fabric-page-info">Hal {curPage} / {totalPages}</span>
              <Button size="icon" variant="outline" className="h-8 w-8 border-taupe" disabled={curPage >= totalPages} onClick={() => setPage(curPage + 1)} data-testid="fabric-next"><ChevronRight className="w-4 h-4" /></Button>
            </div>
          </div>
        )}
      </Panel>

      <FabricDialog open={open} setOpen={setOpen} edit={edit} warehouses={warehouses} onDone={load} />
      <SetStockDialog item={stockFor} setItem={setStockFor} kind="fabric" warehouses={warehouses} onDone={load} />
      <AdjustmentHistoryDialog item={histFor} setItem={setHistFor} warehouses={warehouses} onDone={load} />
      <FabricDetailsDialog fid={detailFor} setFid={setDetailFor} />
      <QuickCuttingDialog state={cutFor} setState={setCutFor} warehouses={warehouses} onDone={load} />
      <FabricCuttingReferencesDialog
        canRecover={user?.role === "owner"}
        color={cuttingReferencesFor}
        onClose={() => setCuttingReferencesFor(null)}
        onRecovered={load}
      />
    </div>
  );
}

function FabricDetailsDialog({ fid, setFid }) {
  const [d, setD] = useState(null);
  useEffect(() => { setD(null); if (fid) api.get(`/production/fabrics/${fid}`).then((r) => setD(r.data)); }, [fid]);
  if (!fid) return null;
  return (
    <Dialog open={!!fid} onOpenChange={() => setFid(null)}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading text-cocoa">Detail Stok Kain — {d?.name}</DialogTitle></DialogHeader>
        {!d ? <div className="py-10 text-center text-wmuted">Memuat...</div> : (
          <div className="space-y-4">
            <div className="grid grid-cols-4 gap-3">
              <div className="rounded-lg border border-taupe/20 p-3 text-center"><div className="text-xs text-wmuted">Available (Yard)</div><div className="text-lg font-heading font-bold text-cocoa">{num(d.available_stock)}</div></div>
              <div className="rounded-lg border border-taupe/20 p-3 text-center"><div className="text-xs text-wmuted">Allocated (Yard)</div><div className="text-lg font-heading font-bold text-cocoa">{num(d.allocated_stock)}</div></div>
              <div className="rounded-lg border border-taupe/20 p-3 text-center"><div className="text-xs text-wmuted">Cutting (Yard)</div><div className="text-lg font-heading font-bold text-cocoa">{num(d.cutting_stock)}</div></div>
              <div className="rounded-lg border border-taupe/20 p-3 text-center"><div className="text-xs text-wmuted">Sisa (Yard)</div><div className="text-lg font-heading font-bold text-cocoa">{num(d.remaining_stock)}</div></div>
            </div>
            <p className="text-xs text-wmuted -mt-2">Dikelompokkan per <b>panjang gulungan</b>. Jumlah gulungan = total yard ÷ panjang; sisa yard non-utuh ditandai sebagai gulungan parsial.</p>
            {(d.colors || []).map((c) => {
              const groups = (c.roll_groups || []).filter((g) => (Number(g.available) || 0) > 0 || (Number(g.cutting) || 0) > 0 || (Number(g.remaining) || 0) > 0);
              const totalGulungan = groups.reduce((s, g) => s + (Number(g.gulungan) || 0), 0);
              return (
                <div key={c.id} className="border border-taupe/20 rounded-lg overflow-hidden">
                  <div className="flex items-center gap-2 px-3 py-2 bg-ivory/50">
                    {c.image ? <img src={c.image} alt="" className="w-8 h-8 rounded object-cover border border-taupe/30" /> : <div className="w-8 h-8 rounded bg-taupe/20" />}
                    <span className="font-medium text-cocoa">{c.color_name}</span>
                    <span className="text-xs text-wmuted">{fmtIDR(c.price_per_yard)}/yd</span>
                    <span className="ml-auto text-sm font-semibold text-cocoa" data-testid={`fabric-detail-summary-${c.id}`}>{totalGulungan} gulungan · {num(c.available_stock)} yd</span>
                  </div>
                  {groups.length ? (
                    <Table>
                      <TableHeader><TableRow className="hover:bg-transparent">
                        <TableHead>Panjang / gulungan</TableHead><TableHead className="text-center">Jml Gulungan</TableHead>
                        <TableHead className="text-right">Available</TableHead><TableHead className="text-right">Cutting</TableHead><TableHead className="text-right">Sisa</TableHead>
                      </TableRow></TableHeader>
                      <TableBody>
                        {groups.map((g, i) => (
                          <TableRow key={i} data-testid={`fabric-detail-roll-${c.id}-${i}`}>
                            <TableCell className="font-mono text-xs">{g.panjang ? `${num(g.panjang)} yd / gulungan` : "-"}</TableCell>
                            <TableCell className="text-center font-semibold text-cocoa">
                              {g.gulungan} gulungan
                              {g.partial_yd ? <span className="block text-[10px] text-amber-600 font-normal">{g.full_rolls} utuh + 1 sisa {num(g.partial_yd)} yd</span> : null}
                            </TableCell>
                            <TableCell className="text-right font-semibold text-cocoa">{num(g.available)}</TableCell>
                            <TableCell className="text-right">{num(g.cutting)}</TableCell>
                            <TableCell className="text-right">{num(g.remaining)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : <div className="px-3 py-3 text-xs text-wmuted">Tidak ada gulungan dengan stok.</div>}
                  {(c.warehouses || []).length > 0 && (
                    <div className="px-3 py-2 text-[11px] text-wmuted border-t border-taupe/10" data-testid={`fabric-detail-perwh-${c.id}`}>
                      <span className="font-semibold text-cocoa">Per gudang:</span> {(c.warehouses || []).map((w) => `${w.warehouse_name}: ${w.gulungan} gulungan (${num(w.available)} yd)`).join(" · ")}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function QuickCuttingDialog({ state, setState, onDone }) {
  const open = !!state;
  const [allFab, setAllFab] = useState([]);
  const [fabDetails, setFabDetails] = useState({});
  const [pq, setPq] = useState("");
  const [prods, setProds] = useState([]);
  const [productId, setProductId] = useState("");
  const [productName, setProductName] = useState("");
  const [pvariants, setPvariants] = useState([]);
  const [rows, setRows] = useState([]);
  const [issueQ, setIssueQ] = useState({});
  const [saving, setSaving] = useState(false);
  const blankFabric = () => ({ fabric_id: state?.fabricId || "", color_id: state?.colorId || "", ypp: "" });
  const blankRow = () => ({ mode: "existing", variant_id: "", color: "", size: "", qty: 0, fabrics: [blankFabric()] });
  useEffect(() => {
    if (!open) return;
    setPq(""); setProds([]); setProductId(""); setProductName(""); setPvariants([]); setIssueQ({});
    setRows([blankRow()]);
    api.get("/production/fabrics").then((r) => setAllFab(r.data));
  }, [open]); // eslint-disable-line
  useEffect(() => {
    if (!pq) { setProds([]); return; }
    const t = setTimeout(() => api.get("/products", { params: { q: pq } }).then((r) => setProds(r.data.slice(0, 8))), 300);
    return () => clearTimeout(t);
  }, [pq]);
  const colorToFabric = React.useMemo(() => { const m = {}; allFab.forEach((f) => (f.colors || []).forEach((c) => { m[c.id] = { fabric_id: f.id, label: `${f.name} - ${c.color_name}` }; })); return m; }, [allFab]);
  const usedFabricIds = React.useMemo(() => { const s = new Set(); rows.forEach((r) => r.fabrics.forEach((f) => { if (f.fabric_id) s.add(f.fabric_id); })); return [...s]; }, [rows]);
  useEffect(() => {
    usedFabricIds.forEach((fid) => { if (fid && !fabDetails[fid]) api.get(`/production/fabrics/${fid}`).then((r) => setFabDetails((p) => ({ ...p, [fid]: r.data }))); });
  }, [usedFabricIds]); // eslint-disable-line
  const rollsForColor = (colorId) => { const info = colorToFabric[colorId]; if (!info) return []; const det = fabDetails[info.fabric_id]; const c = (det?.colors || []).find((x) => x.id === colorId); return (c?.live_rolls || []).filter((r) => (Number(r.available) || 0) > 0); };
  const needByColor = React.useMemo(() => { const m = {}; rows.forEach((r) => r.fabrics.forEach((f) => { if (f.color_id && Number(f.ypp) > 0) m[f.color_id] = (m[f.color_id] || 0) + (parseInt(r.qty) || 0) * Number(f.ypp); })); return m; }, [rows]);
  const usedColors = React.useMemo(() => [...new Set(rows.flatMap((r) => r.fabrics.map((f) => f.color_id).filter(Boolean)))], [rows]);
  const suggested = React.useMemo(() => { const m = {}; usedColors.forEach((cid) => { let need = needByColor[cid] || 0; rollsForColor(cid).forEach((rl) => { const take = Math.min(need, Number(rl.available) || 0); m[`${cid}|${rl.lot}`] = Math.round(take * 100) / 100; need = Math.max(0, need - take); }); }); return m; }, [usedColors, needByColor, fabDetails]); // eslint-disable-line
  const qOf = (k) => (issueQ[k] !== undefined ? issueQ[k] : (suggested[k] ?? 0));
  const setRow = (i, patch) => setRows((rs) => rs.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const setFab = (ri, fi, patch) => setRows((rs) => rs.map((r, idx) => idx === ri ? { ...r, fabrics: r.fabrics.map((f, j) => j === fi ? { ...f, ...patch } : f) } : r));
  const pickVariant = (i, vid) => { if (vid === "new") return setRow(i, { mode: "new", variant_id: "", color: "", size: "" }); const v = pvariants.find((x) => x.id === vid); setRow(i, { mode: "existing", variant_id: vid, color: v?.color || v?.color_id || "", size: v?.size || v?.size_id || "" }); };
  const save = async () => {
    if (!productId) return toast.error("Pilih produk jual dulu");
    const variants = rows.map((r) => ({ variant_id: r.mode === "existing" ? r.variant_id : "", color: r.color, size: r.size, qty: parseInt(r.qty) || 0, fabrics: r.fabrics.filter((f) => f.color_id && Number(f.ypp) > 0).map((f) => ({ fabric_id: f.fabric_id, color_id: f.color_id, yard_per_pcs: Number(f.ypp) })) }))
      .filter((v) => v.qty > 0 && v.fabrics.length);
    if (!variants.length) return toast.error("Isi minimal satu varian dengan target & kain");
    const issues = [];
    usedColors.forEach((cid) => rollsForColor(cid).forEach((rl) => { const q = Number(qOf(`${cid}|${rl.lot}`)) || 0; if (q > 0) issues.push({ color_id: cid, lot: rl.lot, qty: q }); }));
    if (!issues.length) return toast.error("Isi jumlah kain (yard) yang dikeluarkan");
    setSaving(true);
    try {
      const r = await api.post("/production/cutting/quick", { product_id: productId, variants, issues, save_bom: true });
      toast.success(`Cutting ${r.data.cutting_number} dibuat. Lanjut "Catat Hasil Cutting".`);
      setState(null); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  if (!open) return null;
  const totalPcs = rows.reduce((s, r) => s + (parseInt(r.qty) || 0), 0);
  return (
    <Dialog open={open} onOpenChange={() => setState(null)}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="quickcut-dialog">
        <DialogHeader><DialogTitle className="font-heading text-cocoa">Proses Cutting Cepat</DialogTitle><DialogDescription className="text-xs text-wmuted">Pilih produk & varian (warna+ukuran), petakan kain per varian (boleh kombinasi 2 kain), lalu keluarkan roll. BOM + Rencana + Cutting dibuat sekaligus.</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <div><Label>Produk Jual *</Label>
            {productId ? (
              <div className="flex items-center gap-2 text-sm"><span className="font-medium text-cocoa">{productName}</span><Button size="sm" variant="ghost" onClick={() => { setProductId(""); setProductName(""); setPvariants([]); setPq(""); }} className="text-status-reject h-7">Ganti</Button></div>
            ) : (
              <>
                <Input value={pq} onChange={(e) => setPq(e.target.value)} placeholder="Cari produk jual..." data-testid="quickcut-product-query" />
                {prods.length > 0 && <div className="border border-taupe/25 rounded mt-1 max-h-40 overflow-y-auto">{prods.map((p) => <button key={p.id} type="button" className="block w-full text-left px-3 py-1.5 text-sm hover:bg-ivory" onClick={() => { setProductId(p.id); setProductName(p.name); setPvariants(p.variants || []); }} data-testid={`quickcut-product-${p.id}`}>{p.name} <span className="text-wmuted text-xs">({(p.variants || []).length} varian)</span></button>)}</div>}
              </>
            )}
          </div>

          {productId && <div>
            <div className="flex items-center justify-between mb-1"><Label>Varian & Kain (target) — total {totalPcs} pcs</Label></div>
            <div className="space-y-3">
              {rows.map((r, i) => (
                <div key={i} className="border border-taupe/25 rounded-lg p-3 space-y-2" data-testid={`quickcut-variant-${i}`}>
                  <div className="flex flex-wrap gap-2 items-end">
                    <div className="min-w-[11rem]"><Label className="text-xs">Varian</Label>
                      <Select value={r.mode === "existing" ? (r.variant_id || "") : "new"} onValueChange={(v) => pickVariant(i, v)}><SelectTrigger data-testid={`quickcut-variant-sel-${i}`}><SelectValue placeholder="Pilih varian" /></SelectTrigger>
                        <SelectContent>{pvariants.map((v) => <SelectItem key={v.id} value={v.id}>{(v.color || v.color_id || "-")} / {(v.size || v.size_id || "-")}</SelectItem>)}<SelectItem value="new">+ Varian baru</SelectItem></SelectContent></Select>
                    </div>
                    {r.mode === "new" && <>
                      <div><Label className="text-xs">Warna</Label><Input value={r.color} onChange={(e) => setRow(i, { color: e.target.value })} className="w-28 h-9" data-testid={`quickcut-new-color-${i}`} /></div>
                      <div><Label className="text-xs">Ukuran</Label><Input value={r.size} onChange={(e) => setRow(i, { size: e.target.value })} className="w-24 h-9" data-testid={`quickcut-new-size-${i}`} /></div>
                    </>}
                    <div><Label className="text-xs">Qty</Label><Input type="number" value={r.qty} onChange={(e) => setRow(i, { qty: e.target.value })} className="w-24 h-9" data-testid={`quickcut-qty-${i}`} /></div>
                    {rows.length > 1 && <Button size="icon" variant="ghost" onClick={() => setRows((rs) => rs.filter((_, idx) => idx !== i))}><Trash2 className="w-4 h-4 text-status-reject" /></Button>}
                  </div>
                  <div className="pl-1 space-y-1">
                    <div className="text-[11px] text-wmuted">Kain untuk varian ini (boleh &gt; 1 untuk kombinasi):</div>
                    {r.fabrics.map((f, j) => { const fabObj = allFab.find((x) => x.id === f.fabric_id); return (
                      <div key={j} className="flex flex-wrap gap-2 items-center" data-testid={`quickcut-fabric-${i}-${j}`}>
                        <Select value={f.fabric_id} onValueChange={(v) => setFab(i, j, { fabric_id: v, color_id: "" })}><SelectTrigger className="w-44 h-8" data-testid={`quickcut-fabric-sel-${i}-${j}`}><SelectValue placeholder="Kain" /></SelectTrigger>
                          <SelectContent>{allFab.map((x) => <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}</SelectContent></Select>
                        <Select value={f.color_id} onValueChange={(v) => setFab(i, j, { color_id: v })}><SelectTrigger className="w-36 h-8" data-testid={`quickcut-fcolor-sel-${i}-${j}`}><SelectValue placeholder="Warna" /></SelectTrigger>
                          <SelectContent>{(fabObj?.colors || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.color_name}</SelectItem>)}</SelectContent></Select>
                        <Input type="number" value={f.ypp} onChange={(e) => setFab(i, j, { ypp: e.target.value })} placeholder="yd/pcs" className="w-24 h-8" data-testid={`quickcut-ypp-${i}-${j}`} />
                        {r.fabrics.length > 1 && <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setRow(i, { fabrics: r.fabrics.filter((_, k) => k !== j) })}><X className="w-3.5 h-3.5 text-status-reject" /></Button>}
                      </div>
                    ); })}
                    <Button size="sm" variant="ghost" className="text-cocoa h-7" onClick={() => setRow(i, { fabrics: [...r.fabrics, blankFabric()] })} data-testid={`quickcut-add-fabric-${i}`}><Plus className="w-3.5 h-3.5 mr-1" />Kain kombinasi</Button>
                  </div>
                </div>
              ))}
            </div>
            <Button size="sm" variant="outline" className="mt-2 border-taupe text-cocoa" onClick={() => setRows((rs) => [...rs, blankRow()])} data-testid="quickcut-add-variant"><Plus className="w-3.5 h-3.5 mr-1" />Tambah Varian</Button>
          </div>}

          {productId && usedColors.length > 0 && <div>
            <Label>Kain yang Dikeluarkan (pilih gulungan) *</Label>
            <p className="text-[11px] text-wmuted">Isi <b>jumlah gulungan</b> per panjang; sistem otomatis menghitung total yard.</p>
            <div className="space-y-2 mt-1">
              {usedColors.map((cid) => (
                <div key={cid} className="border border-taupe/20 rounded p-2" data-testid={`quickcut-issue-${cid}`}>
                  <div className="text-sm font-medium text-cocoa flex justify-between"><span>{colorToFabric[cid]?.label || cid}</span><span className="text-[11px] text-wmuted">butuh est. {num(needByColor[cid] || 0)} yd</span></div>
                  {rollsForColor(cid).length ? rollsForColor(cid).map((rl) => { const k = `${cid}|${rl.lot}`; const pj = panjangOf(rl.lot); const ydVal = Number(qOf(k)) || 0; const gulReady = pj > 0 ? Math.round((rl.available / pj) * 100) / 100 : rl.available; const gulVal = pj > 0 ? Math.round((ydVal / pj) * 100) / 100 : ydVal; return (
                    <div key={k} className="flex items-center gap-2 text-sm mt-1">
                      <span className="font-mono text-xs w-28">{pj ? `${num(pj)} yd/gulungan` : `lot ${rl.lot || "-"}`}</span>
                      <span className="text-wmuted text-xs">ready {num(gulReady)} gulungan · {num(rl.available)} yd</span>
                      <Input type="number" value={gulVal} onChange={(e) => { const g = parseFloat(e.target.value) || 0; setIssueQ((s) => ({ ...s, [k]: pj > 0 ? Math.round(g * pj * 100) / 100 : g })); }} className="w-20 h-8 ml-auto" data-testid={`quickcut-issueqty-${k}`} />
                      <span className="text-xs text-wmuted">gulungan</span>
                      <span className="text-xs text-cocoa w-24 text-right">= {num(ydVal)} yd</span>
                    </div>
                  ); }) : <div className="text-[11px] text-wmuted mt-1">Tidak ada gulungan ber-stok untuk kain ini.</div>}
                </div>
              ))}
            </div>
          </div>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setState(null)} className="border-taupe text-cocoa">Batal</Button>
          <Button onClick={save} disabled={saving} data-testid="quickcut-save" className="bg-cocoa text-ivory">{saving ? "Menyimpan..." : "Simpan & Keluarkan Kain"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


async function delFabric(f, cb) {
  if (!window.confirm(`Hapus kain ${f.name}?`)) return;
  try { const r = await api.delete(`/production/fabrics/${f.id}`); toast.success(r.data.soft ? "Kain dinonaktifkan (masih ada stok)" : "Kain dihapus"); cb(); }
  catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
}

const emptyRoll = () => ({ yard: "", count: "", available: 0, cutting: 0, remaining: 0 });
const panjangOf = (lot) => { const p = parseFloat(String(lot).split("#")[0]); return isFinite(p) && p > 0 ? p : 0; };
const emptyColor = () => ({ color_name: "", price_per_yard: 0, image: null, rolls: [emptyRoll()] });

function FabricDialog({ open, setOpen, edit, warehouses, onDone }) {
  const [form, setForm] = useState({ name: "", code: "", description: "", product_id: "", product_name: "", supplier_id: "", warehouse_id: "", active: true, colors: [emptyColor()] });
  const [suppliers, setSuppliers] = useState([]);
  const [pq, setPq] = useState("");
  const [products, setProducts] = useState([]);
  useEffect(() => {
    if (!open) return;
    setProducts([]);
    api.get("/suppliers", { params: { active: "active", partner_type: "supplier_kain" } }).then((r) => setSuppliers(r.data));
    if (edit) api.get(`/production/fabrics/${edit.id}`).then((r) => { setForm({ name: r.data.name, code: r.data.code, description: r.data.description || "", product_id: r.data.product_id || "", product_name: r.data.product_name || "", supplier_id: r.data.supplier_id || "", warehouse_id: "", active: r.data.active, colors: (r.data.colors || []).map((c) => ({ id: c.id, color_name: c.color_name, price_per_yard: c.price_per_yard, image: c.image, rolls: (c.live_rolls || []).length ? c.live_rolls.map((lr) => ({ yard: lr.lot, available: lr.available || 0, cutting: lr.cutting || 0, remaining: lr.remaining || 0 })) : ((c.rolls || []).length ? c.rolls : [emptyRoll()]) })) }); setPq(r.data.product_name || ""); });
    else { setForm({ name: "", code: "", description: "", product_id: "", product_name: "", supplier_id: "", warehouse_id: "", active: true, colors: [emptyColor()] }); setPq(""); }
  }, [open, edit]);
  useEffect(() => { const t = setTimeout(() => { if (pq.length >= 2 && !form.product_id) api.get("/products", { params: { q: pq } }).then((r) => setProducts(r.data.slice(0, 12))); else setProducts([]); }, 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [pq]);

  const setColor = (ci, patch) => setForm((f) => ({ ...f, colors: f.colors.map((c, i) => i === ci ? { ...c, ...patch } : c) }));
  const setRoll = (ci, ri, patch) => setForm((f) => ({ ...f, colors: f.colors.map((c, i) => i === ci ? { ...c, rolls: c.rolls.map((r, j) => j === ri ? { ...r, ...patch } : r) } : c) }));
  const onImage = (ci, file) => {
    if (!file) return;
    if (file.size > 500 * 1024) return toast.error("Ukuran gambar maksimal 500kb");
    const reader = new FileReader();
    reader.onload = () => setColor(ci, { image: reader.result });
    reader.readAsDataURL(file);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Nama kain wajib diisi");
    if (!form.colors.length || form.colors.some((c) => !c.color_name.trim())) return toast.error("Nama warna wajib diisi");
    const payload = {
      name: form.name, code: form.code || null, description: form.description, product_id: form.product_id || null, supplier_id: form.supplier_id || null,
      warehouse_id: form.warehouse_id || null, active: form.active,
      colors: form.colors.map((c) => ({ id: c.id, color_name: c.color_name, price_per_yard: parseFloat(c.price_per_yard) || 0, image: c.image,
        rolls: c.rolls.filter((r) => r.yard && (edit || r.count)).map((r) => edit
          ? ({ yard: String(r.yard), available: parseFloat(r.available) || 0, cutting: parseFloat(r.cutting) || 0, remaining: parseFloat(r.remaining) || 0 })
          : ({ yard: String(r.yard), available: (parseFloat(r.yard) || 0) * (parseFloat(r.count) || 0), cutting: 0, remaining: 0 })) })),
    };
    try {
      if (edit) await api.put(`/production/fabrics/${edit.id}`, payload);
      else await api.post("/production/fabrics", payload);
      toast.success("Kain disimpan"); setOpen(false); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading text-cocoa">{edit ? "Edit" : "Buat"} Kain</DialogTitle><DialogDescription className="text-xs text-wmuted">Data kain: nama, kode, supplier, warna & harga. Saat Edit, stok roll bersifat read-only — ubah stok lewat Set Stok.</DialogDescription></DialogHeader>

        <div className="border border-taupe/20 rounded-lg p-4">
          <div className="text-sm font-semibold text-cocoa mb-3">Informasi Kain</div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Nama *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nama kain" data-testid="fabric-name" /></div>
            <div><Label>Kode</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="Otomatis" data-testid="fabric-code" /></div>
            <div><Label>Supplier Kain</Label>
              <Select value={form.supplier_id || "none"} onValueChange={(v) => setForm({ ...form, supplier_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="fabric-supplier"><SelectValue placeholder="Pilih" /></SelectTrigger>
                <SelectContent><SelectItem value="none">- Tanpa Supplier -</SelectItem>{suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Deskripsi</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={1} /></div>
            <div className="relative"><Label>Refer Produk (opsional)</Label>
              <Input value={pq} onChange={(e) => { setPq(e.target.value); setForm({ ...form, product_id: "", product_name: "" }); }} placeholder="Kosongkan utk kain polos" data-testid="fabric-product-search" />
              {form.product_id && <button onClick={() => { setForm({ ...form, product_id: "", product_name: "" }); setPq(""); }} className="absolute right-2 top-8 text-wmuted"><X className="w-4 h-4" /></button>}
              {products.length > 0 && <div className="absolute z-30 mt-1 w-full bg-white border border-taupe/30 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                {products.map((p) => <button key={p.id} onClick={() => { setForm({ ...form, product_id: p.id, product_name: p.name }); setPq(p.name); setProducts([]); }} data-testid={`fabric-product-${p.id}`} className="block w-full text-left px-3 py-2 hover:bg-ivory text-sm">{p.name}</button>)}
              </div>}
            </div>
            {!edit && <div><Label>Gudang Stok Awal</Label>
              <Select value={form.warehouse_id || "none"} onValueChange={(v) => setForm({ ...form, warehouse_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="fabric-warehouse"><SelectValue placeholder="Pilih gudang" /></SelectTrigger>
                <SelectContent><SelectItem value="none">- Pilih -</SelectItem>{warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>}
          </div>
        </div>

        <div className="border border-taupe/20 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-semibold text-cocoa">Warna Kain</div>
            <Button variant="outline" size="sm" className="border-taupe text-cocoa" onClick={() => setForm((f) => ({ ...f, colors: [...f.colors, emptyColor()] }))} data-testid="add-color-btn"><Plus className="w-3.5 h-3.5 mr-1" />Tambah Warna</Button>
          </div>
          {form.colors.map((c, ci) => (
            <div key={ci} className="border border-taupe/20 rounded-lg p-3 mb-3 bg-ivory/30" data-testid={`color-block-${ci}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-wmuted">Warna {ci + 1}</span>
                {form.colors.length > 1 && <Button size="icon" variant="ghost" onClick={() => setForm((f) => ({ ...f, colors: f.colors.filter((_, i) => i !== ci) }))}><X className="w-4 h-4 text-status-rejected" /></Button>}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Nama Warna *</Label><Input value={c.color_name} onChange={(e) => setColor(ci, { color_name: e.target.value })} placeholder="mis. Navy, Putih" data-testid={`color-name-${ci}`} /></div>
                <div><Label>Harga @Yard</Label><Input type="number" value={c.price_per_yard} onChange={(e) => setColor(ci, { price_per_yard: e.target.value })} data-testid={`color-price-${ci}`} /></div>
              </div>
              <div className="mt-2">
                <Label>Foto Kain</Label>
                <div className="flex items-center gap-3 mt-1">
                  {c.image ? <img src={c.image} alt="" className="w-16 h-16 rounded object-cover border border-taupe/30" /> : <div className="w-16 h-16 rounded bg-taupe/20 flex items-center justify-center"><Upload className="w-5 h-5 text-wmuted" /></div>}
                  <input type="file" accept="image/png,image/jpeg" onChange={(e) => onImage(ci, e.target.files[0])} className="text-xs" data-testid={`color-image-${ci}`} />
                  {c.image && <Button size="sm" variant="ghost" onClick={() => setColor(ci, { image: null })} className="text-status-rejected">Hapus</Button>}
                </div>
                <p className="text-[11px] text-wmuted mt-1">JPG/PNG, maks 500kb.</p>
              </div>
              <div className="mt-3">
                <div className="flex items-center justify-between mb-1">
                  <Label>Roll kain (panjang × jumlah gulungan) {edit && <span className="text-[11px] text-wmuted font-normal">— stok live (read-only)</span>}</Label>
                  {!edit && <Button variant="outline" size="sm" className="border-taupe text-cocoa" onClick={() => setColor(ci, { rolls: [...c.rolls, emptyRoll()] })} data-testid={`add-roll-${ci}`}><Plus className="w-3.5 h-3.5 mr-1" />Tambah Roll</Button>}
                </div>
                {edit ? (
                  <>
                    <div className="grid grid-cols-[1fr_1fr_1fr_1fr] gap-2 text-xs text-wmuted px-1"><span>Panjang (yd)</span><span>Available</span><span>Cutting</span><span>Remaining</span></div>
                    {c.rolls.map((r, ri) => (
                      <div key={ri} className="grid grid-cols-[1fr_1fr_1fr_1fr] gap-2 mt-1 items-center">
                        <Input value={r.yard} disabled data-testid={`roll-yard-${ci}-${ri}`} />
                        <Input type="number" value={r.available} disabled data-testid={`roll-avail-${ci}-${ri}`} />
                        <Input type="number" value={r.cutting} disabled />
                        <Input type="number" value={r.remaining} disabled />
                      </div>
                    ))}
                  </>
                ) : (
                  <>
                    <div className="grid grid-cols-[1fr_1fr_auto] gap-2 text-xs text-wmuted px-1"><span>Panjang per gulungan (yd)</span><span>Jumlah gulungan</span><span></span></div>
                    {c.rolls.map((r, ri) => (
                      <div key={ri} className="grid grid-cols-[1fr_1fr_auto] gap-2 mt-1 items-center">
                        <Input placeholder="mis. 100" value={r.yard} onChange={(e) => setRoll(ci, ri, { yard: e.target.value })} data-testid={`roll-yard-${ci}-${ri}`} />
                        <Input type="number" placeholder="mis. 9" value={r.count} onChange={(e) => setRoll(ci, ri, { count: e.target.value })} data-testid={`roll-count-${ci}-${ri}`} />
                        <Button size="icon" variant="ghost" onClick={() => setColor(ci, { rolls: c.rolls.filter((_, j) => j !== ri) })}><Trash2 className="w-4 h-4 text-status-rejected" /></Button>
                      </div>
                    ))}
                    <p className="text-[11px] text-wmuted mt-1">Total yard per baris = Panjang × Jumlah gulungan (mis. 100 × 9 = 900 yd).</p>
                  </>
                )}
                {edit && <p className="text-[11px] text-wmuted mt-1">Catatan: angka di atas adalah <b>stok live</b>. Untuk mengubah stok gunakan tombol <b>Set Stok</b> di daftar kain; Edit hanya untuk data kain (nama, kode, supplier, warna, harga).</p>}
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center gap-2"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /><Label>Aktif</Label></div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} className="border-taupe text-cocoa">Batal</Button>
          <Button onClick={save} data-testid="save-fabric-btn" className="bg-cocoa hover:bg-cocoa-dark text-ivory">Simpan Kain</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const BUCKET_LABEL = { available: "Available", cutting: "Cutting", remaining: "Remaining", allocated: "Allocated", production: "Production" };

export function SetStockDialog({ item, setItem, kind, warehouses, onDone }) {
  const ACC_BUCKETS = [["available", "Available"], ["production", "Production"], ["remaining", "Remaining"]];
  const [wh, setWh] = useState("");
  const [reason, setReason] = useState("Penyesuaian stok");
  const [qty, setQty] = useState(0);
  const [bucket, setBucket] = useState("available");
  const [lot, setLot] = useState("");
  const [rolls, setRolls] = useState([{ yard: "", qty: 0, bucket: "available" }]);
  const [mode, setMode] = useState("add"); // fabric: "add" | "edit"
  const [editRows, setEditRows] = useState([]);
  const [loadingEdit, setLoadingEdit] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (item) { setWh(warehouses[0]?.id || ""); setReason(kind === "fabric" ? "Tambah roll manual" : "Penyesuaian stok"); setQty(0); setBucket("available"); setLot(item.packs?.[0]?.pcs_per_pack || ""); setRolls([{ yard: "", qty: 0, bucket: "available" }]); setMode("add"); setEditRows([]); } }, [item, warehouses, kind]);
  useEffect(() => {
    if (kind !== "fabric" || mode !== "edit" || !item) return;
    setLoadingEdit(true);
    api.get(`/production/fabrics/color/${item.id}/stock-rows`)
      .then((r) => setEditRows((r.data || []).map((x) => ({ ...x, newQty: x.qty }))))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoadingEdit(false));
  }, [mode, item, kind]);
  if (!item) return null;
  const setRoll = (i, patch) => setRolls(rolls.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const total = rolls.reduce((s, r) => s + ((parseFloat(r.yard) || 0) * (parseFloat(r.qty) || 0)), 0);
  const changedEdits = editRows.filter((r) => (parseFloat(r.newQty) || 0) !== (parseFloat(r.qty) || 0));

  const saveAdd = async () => {
    if (!wh) return toast.error("Pilih gudang");
    const rr = rolls.filter((r) => parseFloat(r.qty) > 0 && parseFloat(r.yard) > 0).map((r) => ({ yard: String(r.yard || ""), qty: (parseFloat(r.yard) || 0) * (parseFloat(r.qty) || 0), bucket: r.bucket }));
    if (!rr.length) return toast.error("Isi minimal satu roll");
    setSaving(true);
    try {
      await api.post(`/production/fabrics/color/${item.id}/add-rolls`, { warehouse_id: wh, rolls: rr, reason });
      toast.success("Roll ditambahkan ke stok"); setItem(null); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const saveEdit = async () => {
    if (!changedEdits.length) return toast.error("Tidak ada perubahan nilai roll");
    if (changedEdits.some((r) => (parseFloat(r.newQty) || 0) < 0)) return toast.error("Nilai yard tidak boleh negatif");
    setSaving(true);
    try {
      for (const r of changedEdits) {
        await api.post(`/production/materials/${item.id}/set-stock`, { material_kind: "fabric", warehouse_id: r.warehouse_id, qty: parseFloat(r.newQty) || 0, lot: r.lot || "", bucket: r.bucket, reason: reason || "Edit stok roll" });
      }
      toast.success(`${changedEdits.length} roll diperbarui`); setItem(null); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const saveAccessory = async () => {
    if (!wh) return toast.error("Pilih gudang");
    setSaving(true);
    try {
      await api.post(`/production/materials/${item.id}/set-stock`, { material_kind: kind, warehouse_id: wh, qty: parseFloat(qty) || 0, lot, bucket, reason });
      toast.success("Stok material diperbarui"); setItem(null); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const onSave = kind !== "fabric" ? saveAccessory : (mode === "edit" ? saveEdit : saveAdd);
  const title = kind !== "fabric" ? "Set Stok" : (mode === "edit" ? "Edit Stok Roll" : "Tambah Roll");
  return (
    <Dialog open={!!item} onOpenChange={() => setItem(null)}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="font-heading text-cocoa">{title} — {item.name}</DialogTitle><DialogDescription className="text-xs text-wmuted">Perubahan di sini langsung mengubah stok (ledger).</DialogDescription></DialogHeader>

        {kind === "fabric" && (
          <div className="grid grid-cols-2 gap-1 p-1 bg-ivory/60 rounded-lg mb-1">
            <button type="button" onClick={() => setMode("add")} data-testid="setstock-mode-add" className={`text-sm py-1.5 rounded-md font-medium transition-colors ${mode === "add" ? "bg-cocoa text-ivory" : "text-cocoa hover:bg-taupe/20"}`}>+ Tambah Roll Baru</button>
            <button type="button" onClick={() => setMode("edit")} data-testid="setstock-mode-edit" className={`text-sm py-1.5 rounded-md font-medium transition-colors ${mode === "edit" ? "bg-cocoa text-ivory" : "text-cocoa hover:bg-taupe/20"}`}>Edit / Koreksi Roll</button>
          </div>
        )}

        <div className="grid gap-3">
          {(kind !== "fabric" || mode === "add") && (
            <div><Label>Gudang</Label>
              <Select value={wh} onValueChange={setWh}><SelectTrigger data-testid="setstock-wh"><SelectValue placeholder="Pilih gudang" /></SelectTrigger><SelectContent>{warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent></Select>
            </div>
          )}

          {kind === "fabric" && mode === "add" && (
            <div>
              <div className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 text-[11px] text-wmuted px-1"><span>Panjang / gulungan (yd)</span><span>Jumlah gulungan</span><span>Jenis</span><span></span></div>
              {rolls.map((r, i) => (
                <div key={i} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 mt-1">
                  <Input placeholder="mis. 50" value={r.yard} onChange={(e) => setRoll(i, { yard: e.target.value })} data-testid={`addroll-yard-${i}`} />
                  <Input type="number" placeholder="jml" value={r.qty} onChange={(e) => setRoll(i, { qty: e.target.value })} data-testid={`addroll-qty-${i}`} />
                  <Select value={r.bucket} onValueChange={(v) => setRoll(i, { bucket: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="available">Available</SelectItem><SelectItem value="cutting">Cutting</SelectItem><SelectItem value="remaining">Remaining</SelectItem></SelectContent></Select>
                  <Button size="icon" variant="ghost" onClick={() => setRolls(rolls.filter((_, idx) => idx !== i))}><X className="w-4 h-4 text-status-rejected" /></Button>
                </div>
              ))}
              <div className="flex items-center justify-between mt-2">
                <Button variant="outline" size="sm" className="border-taupe text-cocoa" onClick={() => setRolls([...rolls, { yard: "", qty: 0, bucket: "available" }])} data-testid="addroll-add"><Plus className="w-3.5 h-3.5 mr-1" />Tambah Roll</Button>
                <span className="text-sm">Total: <b className="text-cocoa">{num(total)}</b> yd</span>
              </div>
              <p className="text-[11px] text-wmuted mt-1">Roll ditambahkan (increment) ke stok & terdaftar di master kain.</p>
            </div>
          )}

          {kind === "fabric" && mode === "edit" && (
            <div>
              <p className="text-[11px] text-wmuted mb-2">Ubah langsung <b>TOTAL yard</b> pada roll <b>Available</b> per gudang (kolom "Yard (edit)" = total yard, <b>bukan</b> jumlah roll). Klik ikon hapus untuk menghabiskan roll (set 0). Roll yang sedang dialokasi/cutting/remnant dikelola sistem & tidak bisa diedit di sini.</p>
              {loadingEdit ? <div className="py-6 text-center text-wmuted text-sm">Memuat stok roll...</div> : (
                <>
                  <div className="grid grid-cols-[1fr_1.4fr_1fr_auto] gap-2 text-[11px] text-wmuted px-1"><span>Roll (lot yd)</span><span>Gudang · Jenis</span><span className="text-right">Yard (edit)</span><span></span></div>
                  {editRows.map((r, i) => (
                    <div key={`${r.warehouse_id}|${r.lot}|${r.bucket}`} className="grid grid-cols-[1fr_1.4fr_1fr_auto] gap-2 mt-1 items-center" data-testid={`editroll-row-${i}`}>
                      <span className="font-mono text-xs">{r.lot || "-"} yd</span>
                      <span className="text-xs text-wmuted truncate">{r.warehouse_name} · {BUCKET_LABEL[r.bucket] || r.bucket}</span>
                      <Input type="number" value={r.newQty} onChange={(e) => setEditRows((rs) => rs.map((x, idx) => idx === i ? { ...x, newQty: e.target.value } : x))} className="h-8 text-right" data-testid={`editroll-qty-${i}`} />
                      <Button size="icon" variant="ghost" title="Habiskan roll (set 0)" onClick={() => setEditRows((rs) => rs.map((x, idx) => idx === i ? { ...x, newQty: 0 } : x))} data-testid={`editroll-del-${i}`}><Trash2 className="w-4 h-4 text-status-rejected" /></Button>
                    </div>
                  ))}
                  {!editRows.length && <div className="py-6 text-center text-wmuted text-sm">Tidak ada roll ber-stok untuk diedit. Gunakan "Tambah Roll Baru".</div>}
                  {changedEdits.length > 0 && <p className="text-[11px] text-cocoa mt-2">{changedEdits.length} roll akan diperbarui.</p>}
                </>
              )}
            </div>
          )}

          {kind !== "fabric" && (
            <>
              <div><Label>Pcs/Pack (lot)</Label><Input value={lot} onChange={(e) => setLot(e.target.value)} placeholder="mis. 100" data-testid="setstock-lot" /></div>
              <div><Label>Jenis Stok</Label>
                <Select value={bucket} onValueChange={setBucket}><SelectTrigger data-testid="setstock-bucket"><SelectValue /></SelectTrigger><SelectContent>{ACC_BUCKETS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select>
              </div>
              <div><Label>Qty (set nilai, pcs)</Label><Input type="number" value={qty} onChange={(e) => setQty(e.target.value)} data-testid="setstock-qty" /></div>
            </>
          )}
          <div><Label>Alasan</Label><Input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="setstock-reason" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setItem(null)} className="border-taupe text-cocoa">Batal</Button>
          <Button onClick={onSave} disabled={saving} data-testid="save-setstock-btn" className="bg-cocoa hover:bg-cocoa-dark text-ivory">{saving ? "Menyimpan..." : "Simpan"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
